/**
 * Workspace-level UI preferences that persist through the bonded storage
 * provider and sync across the app.
 *
 * Per-DEVICE opt-ins (they describe this device's behavior, not the project):
 *
 * - "Advanced mode": reveals power-user surfaces in the workspace — today, the
 *   in-workspace Terminal. OFF by default so the IDE stays approachable; the
 *   Workspace subscribes via {@link useAdvancedMode}.
 * - "Keep screen awake": while Synthase is working, hold a screen wake lock so
 *   the device doesn't sleep — a locked/backgrounded page freezes, which makes
 *   the live preview unreachable for Synthase's preview tools. ON by default on
 *   coarse-pointer (phone/tablet) devices, where the screen sleeping mid-build
 *   is exactly the failure — the Wake Lock API needs no user gesture, so no tap
 *   is required; OFF by default on desktops (it costs battery and desktop pages
 *   keep running with the screen off). An explicit toggle either way overrides
 *   the default. The Workspace subscribes via {@link useKeepAwake}.
 *
 * Both values are read DURING render (the Workspace needs them on its first
 * paint), so they go through `getPreferenceStore()` — a synchronous snapshot
 * over the asynchronous `@molecule/app-storage` provider — instead of touching
 * `localStorage`. Swapping the bonded provider therefore redirects these
 * preferences too, and a change in this tab or another tab re-renders every
 * subscriber.
 *
 * @module
 */

import { useEffect, useState } from 'react'

import { getPreferenceStore } from '@molecule/app-storage'

/** Storage key holding the advanced-mode flag (`true`/`false`). */
export const ADVANCED_MODE_KEY = 'molecule_workspace_advanced'

/** Storage key holding the keep-screen-awake flag (`true`/`false`). */
export const KEEP_AWAKE_KEY = 'molecule_workspace_keep_awake'

/**
 * Parses a stored advanced-mode value. Only an explicit on is on: the boolean
 * `true` the provider stores, or a legacy raw `'true'` written by the previous
 * localStorage helper. Anything else (including `null`) is off.
 * @param raw - The stored value.
 * @returns Whether advanced mode is enabled.
 */
export function parseAdvancedMode(raw: unknown): boolean {
  return raw === true || raw === 'true'
}

/**
 * Reads the current advanced-mode flag (defaults to off when storage is
 * unavailable, e.g. SSR or a privacy-locked browser).
 * @returns Whether advanced mode is enabled.
 */
export function getAdvancedMode(): boolean {
  return parseAdvancedMode(getPreferenceStore().get<unknown>(ADVANCED_MODE_KEY))
}

/**
 * Persists the advanced-mode flag and notifies its subscribers.
 * @param enabled - The new value.
 */
export function setAdvancedMode(enabled: boolean): void {
  getPreferenceStore().set(ADVANCED_MODE_KEY, enabled)
}

/**
 * Whether this browser supports the Screen Wake Lock API.
 * @returns True when `navigator.wakeLock` is available.
 */
export function isWakeLockSupported(): boolean {
  return typeof navigator !== 'undefined' && 'wakeLock' in navigator
}

/**
 * The keep-awake default when the user has never toggled it: ON for
 * coarse-pointer (phone/tablet) devices that support the Wake Lock API — a
 * phone screen sleeping mid-build is exactly what makes the live preview
 * unreachable, and acquiring the lock needs no user gesture — and OFF
 * elsewhere, where the lock only costs battery.
 * @returns Whether keep-awake should default to on for this device.
 */
export function getKeepAwakeDefault(): boolean {
  if (!isWakeLockSupported()) return false
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(pointer: coarse)').matches
      : false
  } catch (_error) {
    // matchMedia can throw in odd embeds — treat as a desktop-shaped device.
    return false
  }
}

/**
 * Reads the current keep-awake flag. An explicit `true`/`false` (the stored
 * booleans, or the legacy raw `'true'`/`'false'`) wins; anything else (never
 * set, garbage, storage unavailable) falls back to {@link getKeepAwakeDefault}.
 * @returns Whether keep-awake is enabled on this device.
 */
export function getKeepAwake(): boolean {
  const raw = getPreferenceStore().get<unknown>(KEEP_AWAKE_KEY)
  if (raw === true || raw === 'true') return true
  if (raw === false || raw === 'false') return false
  return getKeepAwakeDefault()
}

/**
 * Persists the keep-awake flag and notifies its subscribers.
 * @param enabled - The new value.
 */
export function setKeepAwake(enabled: boolean): void {
  getPreferenceStore().set(KEEP_AWAKE_KEY, enabled)
}

/**
 * Subscribes a component to one persisted flag.
 *
 * The initial value comes from the synchronous snapshot, so the first render is
 * already correct. A write in this tab re-renders immediately; another tab's
 * write arrives through the provider's `storage` subscription. A provider with
 * no synchronous read hydrates asynchronously and re-renders when the value
 * lands — never a pending promise held across a render.
 * @param key - The storage key.
 * @param read - Reads the flag from the store.
 * @returns The current value.
 */
function usePersistedFlag(key: string, read: () => boolean): boolean {
  const [enabled, setEnabled] = useState<boolean>(read)

  useEffect(() => {
    const store = getPreferenceStore()
    const sync = (): void => setEnabled(read())
    const stop = store.subscribe(key, sync)
    void store.hydrate([key])
    return stop
  }, [key, read])

  return enabled
}

/**
 * Subscribes to the advanced-mode flag — re-renders when it changes in this tab
 * or another.
 * @returns The current advanced-mode value.
 */
export function useAdvancedMode(): boolean {
  return usePersistedFlag(ADVANCED_MODE_KEY, getAdvancedMode)
}

/**
 * Subscribes to the keep-awake flag — re-renders when it changes in this tab or
 * another.
 * @returns The current keep-awake value.
 */
export function useKeepAwake(): boolean {
  return usePersistedFlag(KEEP_AWAKE_KEY, getKeepAwake)
}
